import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import ChatModal from "../chatModal";
import ChatMobile from "../chatMobile";
import { autoLogout } from "../../utils/logoutHelper";
import { useTheme } from "../../providers/ThemeProvider";

const Admin_Header = () => {
  const { theme, toggleTheme } = useTheme();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [messages, setMessages] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [messagesCount, setMessagesCount] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [showChat, setShowChat] = useState(false);
  // Set when some other page (e.g. "Message supplier" on a quotation/order
  // row) asks the header to open Live Chat pre-targeted at one contact,
  // instead of the plain "Chats"/user-list view.
  const [chatPresetUser, setChatPresetUser] = useState(null);

  const handleChatClick = () => {
    setChatPresetUser(null);
    setShowChat(true);
  };
  const handleCloseChat = () => {
    setShowChat(false);
    setChatPresetUser(null);
    // Messages likely got marked read while the panel was open.
    fetchChatUnreadCount();
  };

  // Detect mobile view
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // The header (and thus this Live Chat modal) is already mounted on every
  // admin page via AdminLayout, so other pages don't need their own chat
  // implementation — they just dispatch this event with the contact to open.
  // See openAdminChat() in utils/openAdminChat.js.
  useEffect(() => {
    const openChat = (e) => {
      setChatPresetUser(e?.detail || null);
      setShowChat(true);
    };
    window.addEventListener("open-admin-chat", openChat);
    return () => window.removeEventListener("open-admin-chat", openChat);
  }, []);

  // Logout
  const LogoutAdmin = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You will be logged out!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, logout",
    }).then((result) => {
      if (result.isConfirmed) {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        localStorage.removeItem("Token");
        localStorage.removeItem("user");
        toast.success("Logout Successfully!");
        if (user.user_type === "admin") {
          window.location.replace("/");
        } else if (user.user_type === "supplier") {
          // Was hardcoded to the old thoughtexecuted.com domain, which no
          // longer resolves — the storefront moved to webglowx.com.
          window.location.replace(
            `${process.env.REACT_APP_STOREFRONT_URL || "https://mypackgenie.webglowx.com"}?logout=true`,
          );
        } else {
          window.location.replace("/");
        }
      }
    });
  };

  // Profile
  const handleProfileClick = () => navigate("/admin/profile-update");

  // Notifications
  // This used to fetch once on mount (`useEffect(fetchNotifications, [])`)
  // and never again — the header stays mounted across route changes, so
  // anything that arrived after the page first loaded (a counter-offer
  // sent later in the same session, say) never showed up until a full
  // browser reload remounted this component. Now it also polls in the
  // background and refetches whenever the bell is opened.
  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem("Token");
      if (!token) return;
      const res = await axios.get(
        `${process.env.REACT_APP_URL}/admin/notifications?page=1&limit=25&is_read=false`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = res.data?.data?.notifications || [];
      setMessages(data);
      setNotificationCount(data.length);
    } catch (err) {
      console.error("Error fetching notifications:", err);
      setMessages([]);
      setNotificationCount(0);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // Chat unread badge — the JSX for this already existed (messagesCount)
  // but nothing ever called setMessagesCount, so the badge could never
  // show. /chat/unread-count already existed on the backend too.
  const fetchChatUnreadCount = async () => {
    try {
      const token = localStorage.getItem("Token");
      if (!token) return;
      const res = await axios.get(
        `${process.env.REACT_APP_URL}/chat/unread-count`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setMessagesCount(res.data?.unreadCount || 0);
    } catch (err) {
      console.error("Error fetching chat unread count:", err);
    }
  };

  useEffect(() => {
    fetchChatUnreadCount();
    const interval = setInterval(fetchChatUnreadCount, 20000);
    return () => clearInterval(interval);
  }, []);

  // ChatModal/ChatMobile dispatch this the instant a message actually
  // arrives or gets read (their WebSocket connection now stays open for
  // the whole session, not just while the panel is visible) — react
  // immediately instead of waiting up to 20s for the next poll.
  useEffect(() => {
    const onUnreadChange = () => fetchChatUnreadCount();
    window.addEventListener("chat-unread-changed", onUnreadChange);
    return () =>
      window.removeEventListener("chat-unread-changed", onUnreadChange);
  }, []);

  // Click outside dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Notification click
  const handleNotificationClick = async (note) => {
    try {
      const token = localStorage.getItem("Token");
      if (!token) return;

      await axios.put(
        `${process.env.REACT_APP_URL}/admin/notifications/read/${note.notification_id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setMessages((prev) =>
        prev.filter((n) => n.notification_id !== note.notification_id),
      );
      setNotificationCount((prev) => Math.max(prev - 1, 0));

      if (note.reflect_path) navigate(note.reflect_path);
      setShowDropdown(false);
    } catch (err) {
      console.error("Error marking notification as read:", err);
      toast.error("Failed to mark as read");
    }
  };

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userType = user?.user_type;
  const userDesignation = user?.designation;
  const userName = user?.name || user?.user_type || "User";
  const roleLabel = `${
    userType === "sub_admin" ? "SUB-ADMIN" : userType?.toUpperCase() || ""
  }${userDesignation ? ` (${userDesignation.toUpperCase()})` : ""} PANEL`;

  // Avatar initials
  const initials =
    (userName || "")
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase() || "U";

  // Breadcrumb current-page label, derived from the route
  const segments = pathname.split("/").filter(Boolean);
  const meaningful =
    [...segments].reverse().find((s) => isNaN(Number(s))) || "dashboard";
  const pageTitle = meaningful
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

  // ✅ Ensure jQuery App script binds after header mounts
  useEffect(() => {
    if (window.App && typeof window.App.init === "function") {
      window.App.init();
    }
  }, []);

  return (
    <>
      <style>
        {`
          .uk-hdr{ display:flex; align-items:center; gap:14px; height:100%; width:100%; padding:0 26px; }
          .uk-hdr__btn{
            width:34px; height:34px; border-radius:8px; border:1px solid var(--color-border);
            background:var(--color-surface); color:var(--color-text-secondary);
            display:flex; align-items:center; justify-content:center; cursor:pointer; flex:0 0 34px;
            transition: background .15s ease, color .15s ease, border-color .15s ease;
          }
          .uk-hdr__btn:hover{ background:var(--color-hover); color:var(--color-primary); border-color:var(--color-primary); }
          .uk-hdr__btn svg{ stroke: currentColor; }
          .uk-hdr__crumb{ display:flex; align-items:center; gap:8px; font-size:13px; }
          .uk-hdr__crumb .home{ color:var(--color-text-tertiary); font-weight:500; }
          .uk-hdr__crumb .cur{ color:var(--color-text-primary); font-weight:700; text-transform:capitalize; }
          .uk-hdr__search{ margin-left:6px; flex:1; max-width:420px; }
          .uk-hdr__search .box{
            display:flex; align-items:center; gap:9px; height:38px; padding:0 12px;
            background:var(--color-surface-2); border:1px solid var(--color-border); border-radius:10px;
          }
          .uk-hdr__search input{ border:0; outline:0; background:transparent; font-family:inherit; font-size:13px; color:var(--color-text-primary); width:100%; }
          .uk-hdr__kbd{ display:flex; gap:3px; }
          .uk-hdr__kbd kbd{
            font-family:var(--font-mono); font-size:10px; font-weight:600; color:var(--color-text-tertiary);
            background:var(--color-surface); border:1px solid var(--color-border); border-radius:5px; padding:1px 5px; line-height:1.4;
          }
          .uk-hdr__actions{ margin-left:auto; display:flex; align-items:center; gap:9px; position:relative; }
          .uk-hdr__quick{
            display:flex; align-items:center; gap:7px; height:38px; padding:0 14px; border-radius:10px; border:0;
            background:linear-gradient(135deg,var(--color-primary),var(--color-primary-2)); color:#fff;
            font-family:inherit; font-size:12.5px; font-weight:700; cursor:pointer; white-space:nowrap;
            box-shadow:0 2px 8px rgba(79,70,229,.3); transition:filter .15s ease;
          }
          .uk-hdr__quick:hover{ filter:brightness(1.06); }
          .uk-hdr__quick svg{ stroke:currentColor; }
          .uk-hdr__role{
            font-size:11px; font-weight:700; letter-spacing:.02em; color:var(--color-primary);
            background:var(--color-primary-weak); border-radius:7px; padding:5px 10px; white-space:nowrap;
          }
          .uk-hdr__act{
            position:relative; width:38px; height:38px; border-radius:10px; border:1px solid var(--color-border);
            background:var(--color-surface); color:var(--color-text-secondary);
            display:flex; align-items:center; justify-content:center; cursor:pointer;
            transition: background .15s ease, color .15s ease, border-color .15s ease;
          }
          .uk-hdr__act:hover{ background:var(--color-hover); color:var(--color-primary); border-color:var(--color-primary); }
          .uk-hdr__act svg{ stroke: currentColor; }
          .uk-hdr__badge{
            position:absolute; top:-5px; right:-5px; min-width:17px; height:17px; padding:0 4px; border-radius:9px;
            background:var(--color-danger-text); color:#fff; font-size:9.5px; font-weight:700;
            display:flex; align-items:center; justify-content:center; border:2px solid var(--color-surface);
          }
          .uk-hdr__divider{ width:1px; height:24px; background:var(--color-border); margin:0 2px; }
          .uk-hdr__chip{
            display:flex; align-items:center; gap:9px; height:38px; padding:0 10px 0 4px; border-radius:10px;
            border:1px solid var(--color-border); background:var(--color-surface); cursor:pointer;
          }
          .uk-hdr__chip:hover{ background:var(--color-hover); }
          .uk-hdr__avatar{
            width:30px; height:30px; border-radius:8px; background:linear-gradient(135deg,#6366F1,#8B5CF6);
            color:#fff; display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:700;
          }
          .uk-hdr__menu{
            position:absolute; top:46px; right:0; min-width:200px; background:var(--color-surface);
            border:1px solid var(--color-border); border-radius:12px; box-shadow:var(--shadow-card-hover);
            z-index:1200; overflow:hidden; padding:6px;
          }
          .uk-hdr__menu .head{ padding:10px 12px; border-bottom:1px solid var(--color-border-soft); margin-bottom:4px; }
          .uk-hdr__menu .head .nm{ font-size:13px; font-weight:700; color:var(--color-text-primary); }
          .uk-hdr__menu .head .rl{ font-size:11px; color:var(--color-text-tertiary); }
          .uk-hdr__menu button{
            display:flex; align-items:center; gap:9px; width:100%; text-align:left; padding:9px 12px;
            font-size:13px; font-weight:600; color:var(--color-text-primary); background:transparent;
            border:0; border-radius:8px; cursor:pointer;
          }
          .uk-hdr__menu button:hover{ background:var(--color-hover); }
          .uk-notif{
            position:absolute; top:46px; right:0; width:360px; max-width:88vw; background:var(--color-surface);
            border:1px solid var(--color-border); border-radius:12px; box-shadow:var(--shadow-card-hover); z-index:1200;
          }
          @media (max-width: 991px){
            .uk-hdr__crumb{ display:none; }
            .uk-hdr__search{ display:none; }
            .uk-hdr__quick span{ display:none; }
            .uk-hdr__quick{ padding:0 11px; }
          }
          @media (max-width: 600px){
            .uk-hdr__role{ display:none; }
            .uk-hdr__act.msg{ display:none; }
          }
        `}
      </style>

      {/* Main Header */}
      <div className="header-container fixed-top">
        <header className="header navbar navbar-expand-sm">
          <div className="uk-hdr">
            {/* Sidebar toggle */}
            <button
              type="button"
              className="uk-hdr__btn sidebarCollapse"
              aria-label="Toggle sidebar"
            >
              <svg
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>

            {/* Breadcrumb */}
            <div className="uk-hdr__crumb">
              <span className="home">Home</span>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--color-text-tertiary)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
              <span className="cur">{pageTitle}</span>
            </div>

            {/* Global search (visual) */}
            <div className="uk-hdr__search">
              <div className="box">
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--color-text-tertiary)"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <circle cx="11" cy="11" r="7" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
                <input placeholder="Search orders, users, products…" />
                <span className="uk-hdr__kbd">
                  <kbd>⌘</kbd>
                  <kbd>K</kbd>
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="uk-hdr__actions" ref={dropdownRef}>
              {/* Theme toggle */}
              <button
                type="button"
                className="uk-hdr__act"
                aria-label="Toggle theme"
                title={
                  theme === "dark"
                    ? "Switch to light mode"
                    : "Switch to dark mode"
                }
                onClick={toggleTheme}
              >
                {theme === "dark" ? (
                  <svg
                    width="17"
                    height="17"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="4" />
                    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
                  </svg>
                ) : (
                  <svg
                    width="17"
                    height="17"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9z" />
                  </svg>
                )}
              </button>

              {/* Notifications */}
              <button
                type="button"
                className="uk-hdr__act"
                aria-label="Notifications"
                onClick={() => {
                  if (!showDropdown) fetchNotifications();
                  setShowDropdown(!showDropdown);
                  setOpen(false);
                }}
              >
                <svg
                  width="17"
                  height="17"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                {notificationCount > 0 && (
                  <span className="uk-hdr__badge">{notificationCount}</span>
                )}
              </button>

              {/* Messages / Chat */}
              <button
                type="button"
                className="uk-hdr__act msg"
                aria-label="Messages"
                onClick={handleChatClick}
              >
                <svg
                  width="17"
                  height="17"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                {messagesCount > 0 && (
                  <span className="uk-hdr__badge">{messagesCount}</span>
                )}
              </button>

              <div className="uk-hdr__divider" />

              {/* Profile chip */}
              <button
                type="button"
                className="uk-hdr__chip"
                onClick={() => {
                  setOpen(!open);
                  setShowDropdown(false);
                }}
              >
                <span className="uk-hdr__avatar">{initials}</span>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--color-text-tertiary)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {/* Profile dropdown */}
              {open && (
                <div className="uk-hdr__menu">
                  <div className="head">
                    <div className="nm">{userName}</div>
                    <div className="rl">{roleLabel}</div>
                  </div>
                  <button type="button" onClick={handleProfileClick}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                    Profile
                  </button>
                  <button type="button" onClick={LogoutAdmin}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    Logout
                  </button>
                </div>
              )}

              {/* Notifications dropdown */}
              {showDropdown && (
                <div className="uk-notif">
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "14px 18px",
                      borderBottom: "1px solid var(--color-border-soft)",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "14.5px",
                        fontWeight: 700,
                        color: "var(--color-text-primary)",
                      }}
                    >
                      Notifications
                    </span>
                    <span
                      onClick={() => setShowDropdown(false)}
                      style={{
                        fontSize: "20px",
                        fontWeight: "bold",
                        color: "var(--color-text-secondary)",
                        cursor: "pointer",
                      }}
                    >
                      ×
                    </span>
                  </div>

                  <div
                    style={{
                      maxHeight: "360px",
                      overflowY: "auto",
                      padding: "6px 0",
                    }}
                  >
                    {notificationCount === 0 ? (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "24px",
                          fontSize: "13px",
                          color: "var(--color-text-secondary)",
                        }}
                      >
                        No new notifications
                      </div>
                    ) : (
                      messages.map((note, i) => (
                        <div
                          key={i}
                          style={{
                            display: "flex",
                            padding: "12px 18px",
                            borderBottom: "1px solid var(--color-border-soft)",
                            gap: "12px",
                            cursor: "pointer",
                          }}
                          onClick={() => handleNotificationClick(note)}
                        >
                          <div
                            style={{
                              height: "9px",
                              width: "9px",
                              backgroundColor: "var(--color-primary)",
                              borderRadius: "50%",
                              marginTop: "5px",
                              flex: "0 0 9px",
                            }}
                          ></div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: "13px",
                                fontWeight: 600,
                                color: "var(--color-text-primary)",
                              }}
                            >
                              {note.title}: {note.description}
                            </div>
                            <div
                              style={{
                                fontSize: "11.5px",
                                color: "var(--color-text-tertiary)",
                              }}
                            >
                              {new Date(note.created_at).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Chat Modal */}
        {isMobile ? (
          <ChatMobile
            isOpen={showChat}
            closeModal={handleCloseChat}
            presetUser={chatPresetUser}
          />
        ) : (
          <ChatModal
            isOpen={showChat}
            closeModal={handleCloseChat}
            presetUser={chatPresetUser}
          />
        )}
      </div>
    </>
  );
};

export default Admin_Header;
